import { useState } from "react";
import { toast } from "sonner";
import { Plus, Mail, MessageSquare, Smartphone, Bell, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, DataTable, StatusBadge, Sel, BranchSel, Field, Panel, opts } from "@/components/kit";
import { api, useApi, fmtErr, dt, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NUM = ["first_hour_rate", "succeeding_hour_rate", "flat_rate", "overnight_rate", "daily_max", "grace_minutes", "lost_ticket_penalty"];

function RateForm({ init, onClose, onSaved }) {
  const [f, setF] = useState(init);
  const save = async () => {
    try {
      const body = { ...f, effective_date: new Date(f.effective_date).toISOString() };
      NUM.forEach((k) => (body[k] = Number(body[k] || 0)));
      await api.post("/rates", body);
      toast.success("Rate change submitted for approval");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card" data-testid="rate-dialog">
        <DialogHeader><DialogTitle>{f.supersedes ? "Propose rate change" : "New rate plan"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Branch"><BranchSel value={f.branch_id} onChange={(v) => setF({ ...f, branch_id: v })} all={false} testid="rate-branch" className="w-full" /></Field>
          <Field label="Name" className="sm:col-span-2"><Input data-testid="rate-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Category"><Sel value={f.category} onChange={(v) => setF({ ...f, category: v })} options={opts(["regular", "vip", "tenant", "staff", "event", "promo"])} testid="rate-category" className="w-full" /></Field>
          <Field label="Vehicle"><Sel value={f.vehicle_type} onChange={(v) => setF({ ...f, vehicle_type: v })} options={opts(["car", "motorcycle", "all"])} testid="rate-vehicle" className="w-full" /></Field>
          <Field label="Effective date"><Input type="datetime-local" data-testid="rate-effective" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} /></Field>
          {NUM.map((k) => (
            <Field key={k} label={label(k)}><Input type="number" data-testid={`rate-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
          ))}
        </div>
        <Button data-testid="rate-save" onClick={save} disabled={!f.name}>Submit for approval</Button>
      </DialogContent>
    </Dialog>
  );
}

function Rates() {
  const { branch, can, branchName, branches } = useAuth();
  const { data, loading, reload } = useApi("/rates", { branch_id: branch });
  const [edit, setEdit] = useState(null);
  const now = new Date(Date.now() + 8 * 3600e3).toISOString().slice(0, 16);
  const act = async (r, kind) => {
    try {
      await api.post(`/rates/${r.id}/${kind}`);
      toast.success(`Rate ${kind}d`);
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <>
      <div className="mb-3 flex justify-end">
        {can("manage_rates", "edit") && <Button data-testid="rate-add-btn" onClick={() => setEdit({ branch_id: branch || branches[0]?.id, name: "", category: "regular", vehicle_type: "car", first_hour_rate: 50, succeeding_hour_rate: 20, flat_rate: 0, overnight_rate: 0, daily_max: 0, grace_minutes: 15, lost_ticket_penalty: 300, effective_date: now })}><Plus className="h-4 w-4" /> Rate plan</Button>}
      </div>
      <DataTable testid="rates-table" loading={loading} rows={data} columns={[
        { key: "branch", label: "Branch", render: (r) => branchName(r.branch_id) },
        { key: "name", label: "Plan", render: (r) => <b>{r.name}</b> },
        { key: "category", label: "Category", render: (r) => label(r.category) },
        { key: "vehicle_type", label: "Vehicle" },
        { key: "rates", label: "1st / succ. / flat", render: (r) => <span className="font-mono text-xs">₱{r.first_hour_rate} / ₱{r.succeeding_hour_rate} / ₱{r.flat_rate}</span> },
        { key: "other", label: "Max / night / lost / grace", render: (r) => <span className="font-mono text-xs">₱{r.daily_max} / ₱{r.overnight_rate} / ₱{r.lost_ticket_penalty} / {r.grace_minutes}m</span> },
        { key: "effective_date", label: "Effective", render: (r) => dt(r.effective_date) },
        { key: "created_by", label: "By" },
        { key: "approved_by", label: "Approver" },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
        { key: "act", label: "", render: (r) => (
          <span className="flex gap-2 text-xs">
            {r.status === "pending_approval" && can("approve") && <><button className="text-emerald-500 underline" data-testid={`rate-approve-${r.id}`} onClick={() => act(r, "approve")}>Approve</button><button className="text-red-500 underline" data-testid={`rate-reject-${r.id}`} onClick={() => act(r, "reject")}>Reject</button></>}
            {r.status === "approved" && can("manage_rates", "edit") && <button className="text-primary underline" data-testid={`rate-change-${r.id}`} onClick={() => setEdit({ ...r, supersedes: r.id, effective_date: now, id: undefined })}>Change</button>}
          </span>
        ) },
      ]} />
      {edit && <RateForm init={edit} onClose={() => setEdit(null)} onSaved={reload} />}
    </>
  );
}

function Account() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const sync = useApi("/sync/log", {});
  const save = async () => {
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password changed");
      setF({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const CH = [["In-app", Bell, true], ["Email", Mail, false], ["SMS", Smartphone, false], ["Messenger", MessageSquare, false], ["Viber", MessageSquare, false], ["Push", Bell, false]];
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel title="Change password">
        <div className="space-y-3">
          <Field label="Current password"><Input type="password" data-testid="pw-current" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} /></Field>
          <Field label="New password (min 8)"><Input type="password" data-testid="pw-new" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} /></Field>
          <Button data-testid="pw-save" onClick={save}>Update password</Button>
        </div>
      </Panel>
      <Panel title="Notification channels">
        <div className="grid grid-cols-2 gap-2">{CH.map(([n, I, on]) => (
          <div key={n} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"><span className="flex items-center gap-2"><I className="h-4 w-4" />{n}</span><StatusBadge status={on ? "active" : "future"} tone={on ? "green" : "slate"} /></div>
        ))}</div>
        <p className="mt-3 text-xs text-muted-foreground">Channel adapters are pluggable on the backend (core.CHANNELS). Email/SMS/Messenger/Viber/Push are prepared stubs.</p>
      </Panel>
      <Panel title="Security">
        <ul className="space-y-1.5 text-sm">{["bcrypt password hashing", "JWT sessions expire after 12 hours", "Brute-force lockout (5 attempts / 15 min)", "Per-user permission checks on every API", "Device API keys stored as SHA-256 hashes", "Customer contact data encrypted at rest (Fernet)", "Card data never stored — tokenized gateway only", "Immutable audit trail"].map((x) => (
          <li key={x} className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" />{x}</li>
        ))}</ul>
      </Panel>
      <Panel title="Offline sync log">
        <DataTable testid="sync-log-table" rows={sync.data} empty="No offline transactions synced yet" columns={[
          { key: "client_ref", label: "Ref", render: (r) => <span className="font-mono text-xs">{r.client_ref}</span> }, { key: "kind", label: "Kind" },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> }, { key: "synced_at", label: "Synced", render: (r) => dt(r.synced_at) }]} />
      </Panel>
    </div>
  );
}

export default function Settings() {
  return (
    <div className="fade-up">
      <PageHeader title="Settings" subtitle="Rates (maker-checker approval), notifications, security and offline sync" testid="settings-header" />
      <Tabs defaultValue="rates">
        <TabsList>
          <TabsTrigger value="rates" data-testid="tab-rates">Rate plans</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">Account & system</TabsTrigger>
        </TabsList>
        <TabsContent value="rates" className="mt-4"><Rates /></TabsContent>
        <TabsContent value="account" className="mt-4"><Account /></TabsContent>
      </Tabs>
    </div>
  );
}
